import React, { useRef, useState } from 'react';
import { Download, Copy, Check, Scale, Image as ImageIcon } from 'lucide-react';
import * as htmlToImage from 'html-to-image';

const DEFAULTS = {
  pillText: 'Pre-Mating Planning',
  tagline: 'Pre-mating planning starts with',
  headlineLine1: 'Sample Weigh',
  headlineLine2: 'Your Herd',
  subheadline: 'Get in touch to line up your herd for weighing and condition scoring.',
  bullet1: 'Live-weight capture',
  bullet2: 'Body Condition Score',
  bullet3: 'Clean CSV, same day',
  bullet4: 'Every animal verified',
  contactLabel: 'Line up your herd',
  contactValue: '0800 MEASURE',
  ctaText: 'Get In Touch',
  ctaUrl: 'measuredlivestock.com',
};

/**
 * FB Ad — Pre-mating weighing & condition scoring service promo.
 * Portrait 1080×1350 + Landscape 1200×630.
 */
const MatingWeightsAd = ({ overrides = {} }) => {
  const cfg = Object.fromEntries(
    Object.entries(DEFAULTS).map(([k, v]) => {
      const o = overrides[k];
      return [k, (o !== undefined && o !== null && String(o).trim() !== '') ? o : v];
    })
  );

  const portraitRef = useRef(null);
  const landscapeRef = useRef(null);
  const [downloading, setDownloading] = useState(null);
  const [copied, setCopied] = useState(false);

  const handleDownload = async (ref, filename) => {
    if (!ref.current) return;
    setDownloading(filename);
    try {
      const dataUrl = await htmlToImage.toPng(ref.current, {
        pixelRatio: 2,
        cacheBust: true,
        backgroundColor: '#0a1f13',
      });
      const link = document.createElement('a');
      link.download = filename;
      link.href = dataUrl;
      link.click();
    } catch (err) {
      console.error('Download failed:', err);
      alert('Could not generate the PNG. Try a different browser if it persists.');
    } finally {
      setDownloading(null);
    }
  };

  const adCopy = `${cfg.headlineLine1.toUpperCase()} ${cfg.headlineLine2.toUpperCase()} ⚖️

${cfg.subheadline}

What's included:
✔ ${cfg.bullet1}
✔ ${cfg.bullet2}
✔ ${cfg.bullet3}
✔ ${cfg.bullet4}

Clean data on your platform the same day. No paperwork chasing afterwards.

📞 ${cfg.contactLabel}: ${cfg.contactValue}
👉 ${cfg.ctaUrl}

#NZDairy #MatingWeights #BCS #DairyNZ #MeasuredLivestock #AnimalIDVerified`;

  const handleCopy = () => {
    navigator.clipboard.writeText(adCopy).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const BulletCard = ({ text }) => (
    <div style={{
      background: 'rgba(255,255,255,0.06)',
      border: '1px solid rgba(255,255,255,0.14)',
      borderRadius: 10,
      padding: '14px 16px',
      display: 'flex', alignItems: 'center', gap: 10,
      fontSize: 18, fontWeight: 600,
    }}>
      <span style={{ color: '#ff8533', fontWeight: 900 }}>✓</span>
      {text}
    </div>
  );

  return (
    <section
      className="bg-gradient-to-br from-[#0a1f13] via-[#0f2818] to-[#0a1f13] py-12"
      data-testid="mating-weights-ad-section"
    >
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center mb-8">
          <span className="inline-block bg-[#ff6b00] text-white px-4 py-1 rounded-full text-xs uppercase tracking-widest font-bold mb-3">
            Service Promo · Pre-Mating
          </span>
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-2 flex items-center justify-center gap-3">
            <Scale className="w-9 h-9 text-[#ff6b00]" />
            Mating Weights &amp; Condition
          </h2>
          <p className="text-white/80 max-w-2xl mx-auto">
            Line up your herd for pre-mating weighing &amp; BCS.
          </p>
        </div>

        <div className="grid lg:grid-cols-[1fr_1.4fr] gap-8 items-start mb-8">
          {/* ═══ PORTRAIT 1080 × 1350 ═══ */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <span className="text-white/90 text-sm font-semibold flex items-center gap-2">
                <ImageIcon className="w-4 h-4" /> FB Feed Portrait · 1080 × 1350
              </span>
              <button
                onClick={() => handleDownload(portraitRef, 'measuredlivestock-mating-weights-portrait.png')}
                disabled={downloading === 'measuredlivestock-mating-weights-portrait.png'}
                className="flex items-center gap-1.5 bg-[#ff6b00] hover:bg-[#ff6b00]/90 text-white text-sm font-semibold px-3 py-1.5 rounded transition-all disabled:opacity-60"
                data-testid="download-mating-weights-portrait-btn"
              >
                <Download className="w-4 h-4" />
                {downloading === 'measuredlivestock-mating-weights-portrait.png' ? 'Generating…' : 'Download PNG'}
              </button>
            </div>

            <div className="bg-white rounded-lg shadow-2xl p-2 overflow-hidden">
              <div
                className="origin-top-left"
                style={{
                  transform: 'scale(0.42)',
                  width: 1080, height: 1350,
                  marginRight: -1080 * 0.58, marginBottom: -1350 * 0.58,
                }}
              >
                <div
                  ref={portraitRef}
                  style={{
                    width: 1080, height: 1350, position: 'relative',
                    fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
                    overflow: 'hidden',
                    background: 'radial-gradient(circle at 70% 15%, rgba(255,107,0,0.22) 0%, transparent 50%), linear-gradient(165deg, #163826 0%, #0f2818 45%, #0a1f13 100%)',
                    color: 'white',
                    display: 'flex', flexDirection: 'column',
                    padding: '56px', boxSizing: 'border-box',
                  }}
                >
                  {/* Top row */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <div style={{ width: 5, height: 26, background: '#ff6b00' }} />
                      <span style={{ fontSize: 14, letterSpacing: 5, fontWeight: 700, opacity: 0.92, textTransform: 'uppercase' }}>
                        Measured Livestock
                      </span>
                    </div>
                    <div style={{
                      background: '#ff6b00', color: 'white', padding: '10px 22px', borderRadius: 999,
                      fontSize: 14, fontWeight: 800, letterSpacing: 3, textTransform: 'uppercase',
                    }}>
                      ⚖️ {cfg.pillText}
                    </div>
                  </div>

                  {/* Headline block */}
                  <div style={{ marginTop: 48 }}>
                    <div style={{
                      fontSize: 32, fontWeight: 500, opacity: 0.9, letterSpacing: 0.5,
                      marginBottom: 10, fontStyle: 'italic',
                    }}>
                      {cfg.tagline}
                    </div>
                    <h1 style={{
                      fontSize: 108, lineHeight: 0.98, fontWeight: 900, margin: 0,
                      letterSpacing: -3, textShadow: '0 6px 30px rgba(0,0,0,0.5)',
                    }}>
                      {cfg.headlineLine1}
                    </h1>
                    <h1 style={{
                      fontSize: 108, lineHeight: 0.98, fontWeight: 900, margin: '4px 0 24px',
                      color: '#ff8533', letterSpacing: -3,
                    }}>
                      {cfg.headlineLine2}
                    </h1>
                    <div style={{
                      fontSize: 26, opacity: 0.94, fontWeight: 500, lineHeight: 1.4, maxWidth: 820,
                    }}>
                      {cfg.subheadline}
                    </div>
                  </div>

                  {/* Checklist */}
                  <div style={{ marginTop: 36 }}>
                    <div style={{
                      fontSize: 14, fontWeight: 800, color: '#ff8533',
                      letterSpacing: 4, textTransform: 'uppercase', marginBottom: 14,
                    }}>
                      What's included
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10 }}>
                      <BulletCard text={cfg.bullet1} />
                      <BulletCard text={cfg.bullet2} />
                      <BulletCard text={cfg.bullet3} />
                      <BulletCard text={cfg.bullet4} />
                    </div>
                  </div>

                  {/* Timing selector with cow silhouette - above CTA */}
                  <div style={{
                    position: 'absolute',
                    bottom: 125,
                    left: 56,
                    right: 56,
                    zIndex: 0,
                    pointerEvents: 'none',
                  }}>
                    <div style={{
                      textAlign: 'center',
                      fontSize: 16, fontWeight: 800, color: '#ff8533',
                      letterSpacing: 5, textTransform: 'uppercase', marginBottom: 14,
                    }}>
                      Select Your Time
                    </div>
                    <div style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 18,
                    }}>
                      <div style={{
                        flex: 1,
                        textAlign: 'right',
                        fontSize: 26,
                        fontWeight: 800,
                        color: 'white',
                        lineHeight: 1.15,
                      }}>
                        <div style={{ fontSize: 13, letterSpacing: 2, textTransform: 'uppercase', color: '#ff8533', fontWeight: 700, marginBottom: 4 }}>Option 1</div>
                        Post<br/>AM Milking
                      </div>
                      <img
                        src="/assets/cow-silhouette.png"
                        alt=""
                        aria-hidden="true"
                        crossOrigin="anonymous"
                        style={{
                          width: 340,
                          height: 'auto',
                          opacity: 0.65,
                          filter: 'brightness(0) invert(1)',
                          flexShrink: 0,
                        }}
                      />
                      <div style={{
                        flex: 1,
                        textAlign: 'left',
                        fontSize: 26,
                        fontWeight: 800,
                        color: 'white',
                        lineHeight: 1.15,
                      }}>
                        <div style={{ fontSize: 13, letterSpacing: 2, textTransform: 'uppercase', color: '#ff8533', fontWeight: 700, marginBottom: 4 }}>Option 2</div>
                        Pre or Post<br/>PM Milking
                      </div>
                    </div>
                  </div>

                  {/* Contact strip */}
                  <div style={{
                    position: 'relative', zIndex: 1,
                    marginTop: 24, background: 'rgba(255,107,0,0.14)',
                    border: '2px solid #ff6b00', borderRadius: 14,
                    padding: '18px 20px',
                    display: 'flex', alignItems: 'center', gap: 16,
                  }}>
                    <div style={{
                      background: '#ff6b00', width: 54, height: 54, borderRadius: '50%',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 26, flexShrink: 0,
                    }}>📞</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontSize: 13, fontWeight: 800, color: '#ff8533',
                        letterSpacing: 3, textTransform: 'uppercase', marginBottom: 2,
                      }}>
                        {cfg.contactLabel}
                      </div>
                      <div style={{ fontSize: 32, fontWeight: 800, letterSpacing: 0.5, whiteSpace: 'nowrap', overflow: 'hidden' }}>
                        {cfg.contactValue}
                      </div>
                    </div>
                  </div>

                  {/* CTA */}
                  <div style={{ position: 'relative', zIndex: 1, textAlign: 'center', marginTop: 'auto', paddingTop: 24 }}>
                    <div style={{
                      background: '#ff6b00', color: 'white', padding: '20px 0', borderRadius: 10,
                      fontSize: 30, fontWeight: 800, letterSpacing: 1, textTransform: 'uppercase',
                      boxShadow: '0 14px 32px rgba(0,0,0,0.4)',
                    }}>
                      {cfg.ctaText} · {cfg.ctaUrl}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ═══ LANDSCAPE 1200 × 630 ═══ */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <span className="text-white/90 text-sm font-semibold flex items-center gap-2">
                <ImageIcon className="w-4 h-4" /> FB Feed Landscape · 1200 × 630
              </span>
              <button
                onClick={() => handleDownload(landscapeRef, 'measuredlivestock-mating-weights-landscape.png')}
                disabled={downloading === 'measuredlivestock-mating-weights-landscape.png'}
                className="flex items-center gap-1.5 bg-[#ff6b00] hover:bg-[#ff6b00]/90 text-white text-sm font-semibold px-3 py-1.5 rounded transition-all disabled:opacity-60"
                data-testid="download-mating-weights-landscape-btn"
              >
                <Download className="w-4 h-4" />
                {downloading === 'measuredlivestock-mating-weights-landscape.png' ? 'Generating…' : 'Download PNG'}
              </button>
            </div>

            <div className="bg-white rounded-lg shadow-2xl p-2 overflow-hidden">
              <div
                className="origin-top-left"
                style={{
                  transform: 'scale(0.55)', width: 1200, height: 630,
                  marginRight: -1200 * 0.45, marginBottom: -630 * 0.45,
                }}
              >
                <div
                  ref={landscapeRef}
                  style={{
                    width: 1200, height: 630, position: 'relative',
                    fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
                    overflow: 'hidden',
                    background: 'radial-gradient(circle at 70% 30%, rgba(255,107,0,0.24) 0%, transparent 55%), linear-gradient(110deg, #163826 0%, #0f2818 55%, #0a1f13 100%)',
                    color: 'white',
                    padding: 40, boxSizing: 'border-box',
                    display: 'flex', flexDirection: 'column',
                  }}
                >
                  {/* Timing selector with cow silhouette (landscape) */}
                  <div style={{
                    position: 'absolute',
                    bottom: 72,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    zIndex: 0,
                    pointerEvents: 'none',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                  }}>
                    <div style={{
                      fontSize: 10, fontWeight: 800, color: '#ff8533',
                      letterSpacing: 3, textTransform: 'uppercase', marginBottom: 4,
                    }}>
                      Select Your Time
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{
                        textAlign: 'right',
                        fontSize: 12,
                        fontWeight: 800,
                        color: 'white',
                        lineHeight: 1.1,
                      }}>
                        Post<br/>AM Milking
                      </div>
                      <img
                        src="/assets/cow-silhouette.png"
                        alt=""
                        aria-hidden="true"
                        crossOrigin="anonymous"
                        style={{
                          width: 110,
                          height: 'auto',
                          opacity: 0.65,
                          filter: 'brightness(0) invert(1)',
                          flexShrink: 0,
                        }}
                      />
                      <div style={{
                        textAlign: 'left',
                        fontSize: 12,
                        fontWeight: 800,
                        color: 'white',
                        lineHeight: 1.1,
                      }}>
                        Pre or Post<br/>PM Milking
                      </div>
                    </div>
                  </div>

                  <div style={{ position: 'relative', zIndex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{ width: 5, height: 24, background: '#ff6b00' }} />
                      <span style={{ fontSize: 12, letterSpacing: 4, fontWeight: 700, opacity: 0.92, textTransform: 'uppercase' }}>
                        Measured Livestock
                      </span>
                    </div>
                    <div style={{
                      background: '#ff6b00', color: 'white', padding: '6px 14px', borderRadius: 999,
                      fontSize: 11, fontWeight: 800, letterSpacing: 2, textTransform: 'uppercase',
                    }}>
                      ⚖️ {cfg.pillText}
                    </div>
                  </div>

                  <div style={{ position: 'relative', zIndex: 1, display: 'flex', gap: 28, flex: 1, alignItems: 'center', marginTop: 8 }}>
                    <div style={{ flex: 1.2 }}>
                      <div style={{
                        fontSize: 15, fontWeight: 500, opacity: 0.9, marginBottom: 4,
                        fontStyle: 'italic',
                      }}>{cfg.tagline}</div>
                      <div style={{ fontSize: 54, lineHeight: 1, fontWeight: 900, letterSpacing: -1.4 }}>
                        {cfg.headlineLine1}
                      </div>
                      <div style={{
                        fontSize: 54, lineHeight: 1, fontWeight: 900, color: '#ff8533',
                        letterSpacing: -1.4, margin: '4px 0 14px',
                      }}>{cfg.headlineLine2}</div>
                      <div style={{ fontSize: 14, opacity: 0.94, lineHeight: 1.4, maxWidth: 460 }}>
                        {cfg.subheadline}
                      </div>
                    </div>

                    <div style={{
                      flex: 1,
                      background: 'rgba(255,255,255,0.05)',
                      border: '2px solid rgba(255,107,0,0.55)',
                      borderRadius: 12, padding: 14,
                    }}>
                      <div style={{
                        fontSize: 10, fontWeight: 800, color: '#ff8533',
                        letterSpacing: 2, textTransform: 'uppercase', marginBottom: 10,
                      }}>What's included</div>
                      {[cfg.bullet1, cfg.bullet2, cfg.bullet3, cfg.bullet4].map((t) => (
                        <div key={t} style={{ fontSize: 13, fontWeight: 600, marginBottom: 5, display: 'flex', gap: 8 }}>
                          <span style={{ color: '#ff8533', fontWeight: 900 }}>✓</span>{t}
                        </div>
                      ))}
                      <div style={{
                        borderTop: '1px solid rgba(255,255,255,0.15)', paddingTop: 8, marginTop: 8,
                        fontSize: 11, opacity: 0.9,
                      }}>
                        📞 <strong style={{ color: '#ff8533' }}>{cfg.contactValue}</strong>
                      </div>
                    </div>
                  </div>

                  <div style={{
                    position: 'relative', zIndex: 1,
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12,
                  }}>
                    <div>
                      <div style={{
                        fontSize: 10, letterSpacing: 2, textTransform: 'uppercase',
                        opacity: 0.85, marginBottom: 2,
                      }}>Book online</div>
                      <div style={{ fontSize: 22, fontWeight: 800 }}>{cfg.ctaUrl}</div>
                    </div>
                    <div style={{
                      background: '#ff6b00', color: 'white', padding: '12px 24px', borderRadius: 8,
                      fontSize: 14, fontWeight: 800, letterSpacing: 1, textTransform: 'uppercase',
                      boxShadow: '0 8px 22px rgba(0,0,0,0.4)',
                    }}>
                      {cfg.ctaText} →
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Caption block */}
        <div className="bg-white rounded-xl p-6 shadow-2xl">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <h3 className="font-bold text-[#1a4d2e] flex items-center gap-2 text-lg">
              <Scale className="w-5 h-5 text-[#ff6b00]" />
              Ready-to-Post Caption
            </h3>
            <button
              onClick={handleCopy}
              className="flex items-center gap-2 bg-[#1a4d2e] hover:bg-[#1a4d2e]/90 text-white px-4 py-2 rounded-lg text-sm font-semibold"
              data-testid="copy-mating-weights-ad-copy-btn"
            >
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              {copied ? 'Copied!' : 'Copy Caption'}
            </button>
          </div>
          <pre className="whitespace-pre-wrap text-sm text-[#4a4a4a] bg-[#f7f5f1] border border-[#e5ddd5] p-4 rounded-lg leading-relaxed font-sans">
{adCopy}
          </pre>
          <p className="text-xs text-[#8d6e63] mt-3">
            💡 <strong>Timing:</strong> post 6–8 weeks before mating. Repeat 2–3 weeks later for farmers still planning.
          </p>
        </div>
      </div>
    </section>
  );
};

export default MatingWeightsAd;
